import { Check, Edit } from "@mui/icons-material";
import {
	IconButton,
	InputAdornment,
	TextField,
	TextFieldProps,
	Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";

interface NumericTextFieldProps extends Omit<TextFieldProps, "onChange"> {
	value: number | null | undefined;
	onChange?: (value: number | null) => void;
	showDecimals?: boolean;
	warning?: boolean;
	mustAllowEditable?: boolean;
}

// Format number with thousand separators (1,200.50)
const formatNumber = (
	value: number | null | undefined,
	showDecimals = true,
): string => {
	if (value === null || value === undefined) return "";

	// Ensure non-negative
	const absValue = Math.abs(value);

	const formatted = new Intl.NumberFormat("en-US", {
		minimumFractionDigits: showDecimals ? 2 : 0,
		maximumFractionDigits: showDecimals ? 2 : 0,
	}).format(absValue);
	return formatted;
};

// Parse formatted number string back to number
const parseFormattedNumber = (value: string): number => {
	// Remove comma thousand separators, keep decimal point
	// 1,200.50 -> 1200.50
	const cleaned = value.replace(/,/g, "");
	const parsed = Number(cleaned);
	return isNaN(parsed) || parsed < 0 ? 0 : parsed;
};

export const NumericTextField = React.forwardRef<
	HTMLInputElement,
	NumericTextFieldProps
>(
	(
		{
			value,
			onChange = () => {},
			showDecimals = false,
			warning,
			mustAllowEditable = false,
			disabled,
			onBlur,
			onFocus,
			...props
		},
		ref,
	) => {
		const [displayValue, setDisplayValue] = useState(
			formatNumber(value, false),
		);
		const [hasDecimal, setHasDecimal] = useState(true);
		const [isEditable, setIsEditable] = useState(!mustAllowEditable);

		// Update display when value changes externally
		useEffect(() => {
			setDisplayValue(formatNumber(value, hasDecimal && showDecimals));
		}, [value, showDecimals, hasDecimal]);

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const inputValue = e.target.value;

			// Only allow digits, comma, and dot
			const filtered = inputValue.replace(/[^0-9.,]/g, "");

			// Check if user typed a decimal point
			const hasDecimalPoint = filtered.includes(".");
			setHasDecimal(hasDecimalPoint);

			setDisplayValue(filtered);
			const parsed = parseFormattedNumber(filtered);
			onChange(parsed || null);
		};

		const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
			// Reformat on blur to ensure proper formatting
			const inputValue = e.target.value;
			const parsed = parseFormattedNumber(inputValue);

			// Only show decimals if user typed a decimal point
			const hasDecimalPoint = inputValue.includes(".");
			setHasDecimal(hasDecimalPoint);
			setDisplayValue(formatNumber(parsed, hasDecimalPoint && showDecimals));
			onChange(parsed || null);
			onBlur?.(e);
		};

		const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
			// Show raw value on focus for easier editing
			setDisplayValue(String(parseFormattedNumber(displayValue)));
			onFocus?.(e);
			setTimeout(() => {
				e?.target.select();
			}, 0);
		};

		return (
			<TextField
				ref={ref}
				type="text"
				value={displayValue}
				onChange={props["aria-readonly"] ? () => {} : handleChange}
				onBlur={handleBlur}
				onFocus={handleFocus}
				margin="none"
				disabled={!isEditable || disabled}
				slotProps={{
					htmlInput: {
						"data-test-id": props.label
							?.toString()
							.toLowerCase()
							.replaceAll(" ", "_"),
					},
					input: {
						endAdornment: mustAllowEditable ? (
							<InputAdornment position="end">
								<Tooltip title={isEditable ? "Lock Editing" : "Enable Editing"}>
									<IconButton
										onClick={() => setIsEditable(!isEditable)}
										edge="end"
										size="small"
									>
										{!isEditable ? (
											<Edit sx={{ fontSize: "1rem" }} />
										) : (
											<Check sx={{ fontSize: "1rem" }} />
										)}
									</IconButton>
								</Tooltip>
							</InputAdornment>
						) : undefined,
					},
					formHelperText: {
						sx: {
							color: (theme) =>
								`${
									warning
										? theme.palette.warning.main
										: theme.palette.primary.main
								} ${!props.error ? "!important" : ""}`,
						},
					},
				}}
				{...props}
				helperText={props["aria-readonly"] ? "" : props["helperText"]}
			/>
		);
	},
);

NumericTextField.displayName = "NumericTextField";
